/* eslint-disable complexity */
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import SimpleDivider from '../../../../Components/Dividers/SimpleDivider';
import TextInput from '../../../../Components/Inputs/TextInput';
import Title from '../../../../Components/Labels/Title';

import {
  TYPE_OF_IDENT_OPTIONS,
  PAYMENT_METHOD,
  TYPE_OF_CUSTOMER
} from '../../payment_enums';
import { ROUTE_NAMES } from '../../../../Routes/Routes';
import {
  TERMS,
  TERMS_ROUTES
} from '../../../Information/Information/Information';
import { UserContext } from '../../../../Contexts/UserContext';
import FlagColombia from '../../../../Components/CustomIcons/Flags/FlagColombia';

import { PrivacyPoliciesLink } from '../../../../Configs/Links';
import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import ControlledNumberInput from '../../../../Components/Controlled/ControlledNumberInput';
import ControlledSelectInput from '../../../../Components/Controlled/ControlledSelect';
import { IDENTIFICATION_TYPE } from '../../../../Enums/users';

const BrebInfoFormGDC = props => {
  const {
    loading = false,
    setPayerInfo,
    setDisabledNext,
    showTitle = true,
    extraPadding = false,
    disabled = false
  } = props;
  // * CONTEXTS
  const currentUser = useContext(UserContext);

  // * OTHER HOOKS
  const classes = useStyles();

  const {
    register,
    errors,
    watch,
    control,
    formState: { isValid },
    setValue
  } = useFormContext();

  const {
    email,
    firstName,
    lastName,
    typeOfIdentification,
    identification,
    phoneCountryCode,
    phone,
    typeOfPerson
  } = watch([
    'email',
    'firstName',
    'lastName',
    'typeOfIdentification',
    'identification',
    'phoneCountryCode',
    'phone',
    'typeOfPerson'
  ]);

  const [acceptsTerms, setAcceptsTerms] = useState(Boolean(currentUser));

  const onChangeAcceptTerms = useCallback(() => {
    if (currentUser) {
      return;
    }

    setAcceptsTerms(!acceptsTerms);
  }, [currentUser, acceptsTerms]);

  useEffect(() => {
    if (currentUser) {
      setDisabledNext(!isValid);
    } else {
      setDisabledNext(!isValid || !acceptsTerms);
    }
  }, [acceptsTerms, setDisabledNext, isValid, currentUser]);

  useEffect(() => {
    if (typeOfIdentification === IDENTIFICATION_TYPE.NIT) {
      setValue('typeOfPerson', TYPE_OF_CUSTOMER[1].value);
    } else {
      setValue('typeOfPerson', TYPE_OF_CUSTOMER[0].value);
    }

    setPayerInfo(prev => ({
      ...prev,
      type: PAYMENT_METHOD.BREB,
      email,
      firstName,
      lastName,
      typeOfIdentification,
      identification,
      phone,
      phoneCountryCode,
      typeOfPerson
    }));
  }, [
    email,
    firstName,
    lastName,
    typeOfIdentification,
    identification,
    phone,
    phoneCountryCode,
    typeOfPerson,
    acceptsTerms,
    setPayerInfo,
    setValue
  ]);

  return (
    <>
      <div
        className={clsx(classes.formRoot, {
          [classes.extraPaddingBottom]: extraPadding
        })}
      >
        {showTitle && (
          <Title text="Información del pago" className={classes.pageTitle} />
        )}
        <Grid container spacing={4} className={classes.formContainer}>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="email"
              placeholder="correo@ejemplo.com"
              name="email"
              error={Boolean(errors.email)}
              helperText={errors.email && errors.email.message}
              disabled={loading || disabled}
              label="Correo electrónico"
              className={
                errors && Boolean(errors.email)
                  ? classes.inputError
                  : classes.input
              }
              margin="none"
              fullWidth
              inputRef={register}
              autoFocus
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="firstName"
              name="firstName"
              error={Boolean(errors.firstName)}
              defaultValue={(currentUser && currentUser.firstName) || ''}
              helperText={errors.firstName && errors.firstName.message}
              disabled={loading || disabled}
              label="Nombres del pagador"
              className={
                errors && Boolean(errors.firstName)
                  ? classes.inputError
                  : classes.input
              }
              margin="none"
              fullWidth
              inputRef={register}
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="lastName"
              name="lastName"
              error={Boolean(errors.lastName)}
              defaultValue={(currentUser && currentUser.lastName) || ''}
              helperText={errors.lastName && errors.lastName.message}
              disabled={loading || disabled}
              label="Apellidos del pagador"
              className={
                errors && Boolean(errors.lastName)
                  ? classes.inputError
                  : classes.input
              }
              margin="none"
              fullWidth
              inputRef={register}
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <ControlledSelectInput
              control={control}
              name="typeOfPerson"
              defaultValue="N"
              options={TYPE_OF_CUSTOMER}
              id="typeOfPerson"
              label="Tipo de cliente"
              error={Boolean(errors.typeOfPerson)}
              helperText={errors.typeOfPerson && errors.typeOfPerson.message}
              margin="none"
              fullWidth
              disabled={true}
              className={
                errors && Boolean(errors.typeOfPerson)
                  ? classes.inputError
                  : classes.input
              }
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <ControlledSelectInput
              control={control}
              name="typeOfIdentification"
              defaultValue={
                (currentUser && currentUser.identificationType) || 'CC'
              }
              options={TYPE_OF_IDENT_OPTIONS}
              id="typeOfIdentification"
              label="Tipo de identificación"
              error={Boolean(errors.typeOfIdentification)}
              helperText={
                errors.typeOfIdentification &&
                errors.typeOfIdentification.message
              }
              margin="none"
              fullWidth
              disabled={loading || disabled}
              className={
                errors && Boolean(errors.typeOfIdentification)
                  ? classes.inputError
                  : classes.input
              }
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="identification"
              name="identification"
              error={Boolean(errors.identification)}
              helperText={
                errors.identification && errors.identification.message
              }
              disabled={loading || disabled}
              label="Número de identificación"
              className={
                errors && Boolean(errors.identification)
                  ? classes.inputError
                  : classes.input
              }
              margin="none"
              fullWidth
              inputRef={register}
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <ControlledNumberInput
              control={control}
              name="phoneCountryCode"
              defaultValue={
                currentUser && currentUser.phoneCountryCode
                  ? currentUser.phoneCountryCode
                  : '57'
              }
              id="BrebInfoForm_input_phoneCountryCode"
              autoComplete="tel-country-code"
              InputProps={{
                startAdornment: (
                  <FlagColombia size={20} style={{ flexShrink: 0 }} />
                )
              }}
              format="+###"
              placeholder="57"
              fullWidth
              label="Prefijo telefónico"
              error={Boolean(errors.phoneCountryCode)}
              helperText={
                errors.phoneCountryCode && errors.phoneCountryCode.message
              }
              margin="none"
              className={
                errors && Boolean(errors.phoneCountryCode)
                  ? classes.inputError
                  : classes.input
              }
              disabled={loading || disabled}
            />
          </Grid>
          <Grid item sm={4} xs={12} className={classes.inputContainer}>
            <ControlledNumberInput
              control={control}
              name="phone"
              id="BrebInfoForm_input_phone"
              autoComplete="tel-local"
              fullWidth
              margin="none"
              label="Número celular"
              error={Boolean(errors.phone)}
              helperText={errors.phone && errors.phone.message}
              disabled={loading || disabled}
            />
          </Grid>
          {!Boolean(currentUser) && (
            <Grid item xs={12} className={classes.termsContainer}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="acceptsTerms"
                    checked={acceptsTerms}
                    onClick={onChangeAcceptTerms}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    disabled={Boolean(currentUser)}
                    value="checkedI"
                  />
                }
                label={
                  <Typography className={classes.terms}>
                    He leído y acepto los{' '}
                    <a
                      className={classes.link}
                      href={`${ROUTE_NAMES.information}/${
                        TERMS_ROUTES[TERMS.introduction]
                      }`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      términos y condiciones
                    </a>{' '}
                    de uso, la{' '}
                    <a
                      href={PrivacyPoliciesLink}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      política y aviso de privacidad
                    </a>{' '}
                    y autorizo el tratamiento de mis datos personales.
                  </Typography>
                }
              />
            </Grid>
          )}
        </Grid>
        <SimpleDivider />
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  formRoot: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(3.5)
  },
  extraPaddingBottom: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      paddingBottom: theme.spacing(6)
    }
  },
  pageTitle: {
    marginTop: 0,
    fontSize: 14,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      fontSize: 16
    }
  },
  formContainer: {
    paddingTop: theme.spacing(2)
  },
  inputContainer: {
    marginBottom: theme.spacing(1.2),
    paddingBottom: '0 !important',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(1.2),
      paddingTop: '0 !important'
    }
  },
  termsContainer: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingTop: '0 !important'
    }
  },
  terms: {
    fontSize: 14
  },
  input: {
    marginBottom: 17
  },
  inputError: {
    marginBottom: 0
  }
}));

export default BrebInfoFormGDC;
